import React, { useState, useEffect } from "react";
import {
  CarbonReporterBase,
  Description,
  Reports,
  Report,
  Icon,
  Amount,
  Units,
} from "./carbon-reporter.styled";
import Countup from "react-countup";
import VisibilitySensor from "react-visibility-sensor";
import { getDotNeutralData } from "../../utils/apiRequests";
import { roundLbsOffset } from "../../utils/roundLbsOffset";
import { CarbonOffsetSliceFragment } from "../../types/graphql";
import { SliceComponentProps } from "@prismicio/react";
import { Slice } from "@prismicio/types";
import { IPageContext } from "../../types/types";
import { SharedSliceType } from "../shared-slice/shared-slice";
import * as FA from "../../styles/fontawesome";

interface IPureCarbonReporterProps {
  lbsOffset: number;
  content: CarbonOffsetSliceFragment;
}

export const PureCarbonReporter = (props: IPureCarbonReporterProps) => {
  const lbsOffset = props.lbsOffset;
  let [viewed, setViewed] = useState(false);

  // Get rounded amount and suffix from roundLbsOffset
  const rounded = roundLbsOffset(lbsOffset);
  const roundedLbsOffset = rounded.roundedLbsOffset;
  const suffix = rounded.suffix;

  // Calculate number of smartphones charged
  // https://www.epa.gov/energy/greenhouse-gases-equivalencies-calculator-calculations-and-references#smartphones
  const smartphoneConstant = 57.8;
  const numSmartphonesCharged = (lbsOffset * smartphoneConstant) / 1000000;

  const header = props.content.primary?.header?.text || "";
  const text = props.content.primary?.text?.text || "";
  return (
    <CarbonReporterBase>
      <Description>
        <h3>{header}</h3>
        <p>{text}</p>
      </Description>
      <VisibilitySensor
        onChange={(isViewed: boolean) => (isViewed ? setViewed(true) : null)}
        offset={{ top: 84 }}
      >
        <Reports>
          <Report>
            <Icon css={FA.LeafRegular} />
            <Countup
              start={0.0}
              end={viewed ? roundedLbsOffset : 0.0}
              decimals={1}
              duration={3}
              suffix={suffix}
            >
              {({ countUpRef }) => <Amount ref={countUpRef}></Amount>}
            </Countup>

            <Units>
              LBS OF CO<sub>2</sub>&nbsp;OFFSET
            </Units>
          </Report>
          <Report>
            <Icon css={FA.MobileScreenButtonRegular} />
            <Countup
              start={0.0}
              end={viewed ? numSmartphonesCharged : 0.0}
              decimals={1}
              duration={3}
              suffix={"M"}
            >
              {({ countUpRef }) => <Amount ref={countUpRef}></Amount>}
            </Countup>
            <Units>SMARTPHONES CHARGED</Units>
          </Report>
        </Reports>
      </VisibilitySensor>
    </CarbonReporterBase>
  );
};

export type ICarbonReporterProps = Slice<
  SharedSliceType,
  CarbonOffsetSliceFragment["primary"]
> & { type: SharedSliceType.carbon_offset };

export const CarbonReporter = ({
  slice,
  context,
}: SliceComponentProps<ICarbonReporterProps, IPageContext>) => {
  const [lbsOffset, setLbsOffset] = useState(0);
  useEffect(() => {
    getDotNeutralData()
      .then((res) => {
        let totalLbs = res.offsets.total_lbs;
        setLbsOffset(totalLbs);
      })
      .catch(() => {
        // If an error occurs, use harcoded data (as of 7/17/2020)
        setLbsOffset(1100000);
      });
  }, []);

  return <PureCarbonReporter content={slice} lbsOffset={lbsOffset} />;
};
